import classNames from 'classnames';
import React, { useContext, useEffect } from 'react';

import { useConverters } from './../../pm-converters/context';
import { TIPTAP_CAPTION_TYPE } from './../../ricos-content';
import { PluginsEventsContext, RicosContext, usePluginExportsContext } from './../../ricos-context';
import type { ImageNode } from './../../ricos-schema/next-schema';
import type { PluginProps } from './../../ricos-types';
import { Node_Type } from './../../ricos-types';
import { isNodeSelected } from './../../_shared/is-node-selected';
import { getPluginContainerClassNames, getPluginFocusClassNames } from './../../_shared/node-styles';

import { CAPTION } from '../data-hooks';
import classes from './caption-scss';
import { useEditorSelection } from './use-editor-selection';

export const Caption: React.FC<PluginProps> = ({ NodeViewContent, editor, node }) => {
  const { isMobile } = useContext(RicosContext);
  const pluginsEvents = useContext(PluginsEventsContext);
  const pluginExports = usePluginExportsContext();
  const selectedNodes = useEditorSelection(editor).selectedNodes;
  const captionNodePos = editor.$doc.querySelector(TIPTAP_CAPTION_TYPE, {
    id: node.attrs.id,
  });
  const converters = useConverters();
  if (!captionNodePos?.parent?.node) {
    console.error('Caption must have a parent.');
    return null;
  }
  const ricosParentNode = converters.fromPmNode(captionNodePos.parent.node.toJSON());

  const AiButton = pluginExports.aiIntegrations?.image?.captionTrigger;

  const isCaptionSelected = selectedNodes.find(
    selectedNode => selectedNode.attrs.id === node.attrs?.id
  );
  const isParentSelected = selectedNodes.find(
    selectedNode => selectedNode.attrs.id === ricosParentNode?.id
  );

  useEffect(() => {
    if (node.content.firstChild?.text || isCaptionSelected || isParentSelected) {
      return;
    }
    pluginsEvents?.publishPluginLayoutShift({ nodeId: node.attrs.id });
  }, [isCaptionSelected, isParentSelected]);

  const isVisible = isCaptionSelected || isParentSelected || !!node.content.firstChild;
  const hasAiButton = isCaptionSelected && ricosParentNode?.type === Node_Type.IMAGE && AiButton;

  const isFocused = isNodeSelected(editor.state, node.attrs.id);

  const className = classNames(
    classes.caption,
    getPluginContainerClassNames(isMobile),
    getPluginFocusClassNames(isFocused)
  );

  /*
   * Display none is crucial here to prevent issues in older browser versions, specifically Chrome 113, which is used
   * in the Mobile App WebView. Adding new HTML elements to the DOM trigger ProseMirror's readDOMChange function,
   * causing it to misinterpret the update as a user action (e.g. pressing Enter) which triggers keyboard shortcuts.
   * https://github.com/ProseMirror/prosemirror-view/blob/f37ebb29befdbde3cd194fe13fe17b78e743d2f2/src/domchange.ts#L127
   * By setting display: 'none', we ensure the component remains in the DOM without being visually rendered
   * avoiding ProseMirror's DOM change detection.
   */

  return (
    <div
      className={className}
      style={
        isVisible
          ? undefined
          : {
              display: 'none',
            }
      }
    >
      {hasAiButton ? (
        <div className={classes.aiButtonContainer}>
          <AiButton
            node={ricosParentNode as ImageNode}
            onCaptionChange={caption => {
              const { range } = captionNodePos;
              const { tr } = editor.state;

              tr.insertText(caption, range.from, range.to);
              editor.view.dispatch(tr);
            }}
          />
        </div>
      ) : null}
      <NodeViewContent
        dir="auto"
        data-hook={CAPTION}
        as="figcaption"
        style={{
          width: '100%',
          overflow: 'auto',
          lineHeight: node.attrs?.textStyle?.lineHeight,
          textAlign: node.attrs?.textStyle?.textAlignment,
          paddingTop: node.attrs?.style?.paddingTop ?? node.attrs?.styleDefaults?.paddingTop,
          paddingBottom:
            node.attrs?.style?.paddingBottom ?? node.attrs?.styleDefaults?.paddingBottom,
        }}
      />
    </div>
  );
};
